/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/alt-text */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import Moment from "react-moment";
import { toast } from "react-toastify";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import { useConfirmDialog } from "../../services/useConfirmDialog";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import { ENDPOINT } from "../../services/api-endpoint";
import {
  addList,
  getList,
  getListById,
  postSkill,
  updateList,
} from "../../services/services";
import "./mes-operators.scss";

type OperatorRow = {
  id: number;
  operatorid?: string | null;
  fullname?: string | null;
  skills?: string | null;
  experienceyears?: number | string | null;
  createdate?: string;
  active?: boolean;
};

type SkillRow = {
  id: number;
  skillname: string;
  iscustom?: boolean | null;
};

function parseSkillsCsv(raw: string | null | undefined): string[] {
  if (!raw || !String(raw).trim()) return [];
  return uniqueSkillList(
    String(raw)
      .split(/[,;]/g)
      .map((s) => s.trim())
      .filter(Boolean),
  );
}

function uniqueSkillList(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of list) {
    const t = s.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function serializeSkills(list: string[]): string {
  return uniqueSkillList(list).join(", ");
}

function skillDomId(skill: string, prefix: string): string {
  return `${prefix}-${skill.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

function formatExperience(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  const n = typeof val === "number" ? val : parseFloat(String(val));
  if (Number.isNaN(n)) return "—";
  const r = Math.round(n);
  return `${r} yrs`;
}

function normalizeListPayload(raw: unknown): OperatorRow | null {
  if (!raw) return null;
  if (Array.isArray(raw)) return (raw[0] as OperatorRow) ?? null;
  return raw as OperatorRow;
}

const MesOperators = () => {
  const { isLoading, runWithLoading } = usePageLoader();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [validated, setValidated] = useState(false);
  const [busyLabel, setBusyLabel] = useState("Loading operators…");

  const [searchText, setSearchText] = useState<string>("");
  const [pageSize] = useState<number>(10);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [operators, setOperators] = useState<OperatorRow[]>([]);
  const [openAddUpdateModal, setAddUpdateModal] = useState<boolean>(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [formInputs, setForm] = useState({
    operatorid: "",
    fullname: "",
    experienceYears: "",
  });
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [customDraft, setCustomDraft] = useState("");
  const [skillCatalog, setSkillCatalog] = useState<SkillRow[]>([]);

  const searchDebounceRef = useRef<number>();

  const fetchSkillsCatalog = useCallback(async () => {
    try {
      const rows = await getList(ENDPOINT.SKILL_LIST);
      setSkillCatalog(Array.isArray(rows) ? (rows as SkillRow[]) : []);
    } catch (e) {
      console.log(`Error loading skills: ${e}`);
      toast.error("Could not load skills catalog.");
    }
  }, []);

  const catalogNamesLower = useMemo(
    () => new Set(skillCatalog.map((s) => s.skillname.toLowerCase())),
    [skillCatalog],
  );

  const extrasSelected = useMemo(
    () => selectedSkills.filter((s) => !catalogNamesLower.has(s.toLowerCase())),
    [selectedSkills, catalogNamesLower],
  );

  const handleClose = () => {
    setValidated(false);
    setEditId(null);
    setAddUpdateModal(false);
    setForm({ operatorid: "", fullname: "", experienceYears: "" });
    setSelectedSkills([]);
    setCustomDraft("");
  };

  const handleShow = () => {
    setValidated(false);
    setEditId(null);
    setForm({ operatorid: "", fullname: "", experienceYears: "" });
    setSelectedSkills([]);
    setCustomDraft("");
    void fetchSkillsCatalog();
    setAddUpdateModal(true);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onLoad = useCallback(
    async (nextPageIndex: number = 0, overrideSearchText?: string) => {
      try {
        await runWithLoading(async () => {
          const effectiveSearchText =
            overrideSearchText !== undefined ? overrideSearchText : searchText;

          const resp = await getList(ENDPOINT.OPERATOR_LIST, {
            pagesize: pageSize,
            pagenumber: nextPageIndex,
            serachtext: effectiveSearchText,
          });

          const items = Array.isArray(resp) ? resp : (resp as { data?: OperatorRow[] })?.data || [];
          const count = Array.isArray(resp)
            ? items.length
            : Number((resp as { count?: number })?.count || 0);

          setOperators(items as OperatorRow[]);
          setTotalCount(count);
          setPageIndex(nextPageIndex);
        });
      } catch (e) {
        console.log(`Error in getOperators: ${e}`);
        toast.error("Could not load operators.");
      }
    },
    [searchText, pageSize, runWithLoading],
  );

  useEffect(() => {
    void onLoad(0, "");
    void fetchSkillsCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleSearchReload = useCallback(
    (value: string) => {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = window.setTimeout(() => {
        void onLoad(0, value);
      }, 320);
    },
    [onLoad],
  );

  const toggleSkill = (skill: string) => {
    setSelectedSkills((prev) => {
      const key = skill.toLowerCase();
      const has = prev.some((s) => s.toLowerCase() === key);
      if (has) return prev.filter((s) => s.toLowerCase() !== key);
      return [...prev, skill];
    });
  };

  const addCustomSkill = async () => {
    const t = customDraft.trim();
    if (!t) return;
    setBusyLabel("Adding skill…");
    try {
      await runWithLoading(async () => {
        const created = await postSkill({ skillname: t });
        await fetchSkillsCatalog();
        const name = (created.skillname ?? t).trim();
        setSelectedSkills((prev) => uniqueSkillList([...prev, name]));
        setCustomDraft("");
      });
      toast.success("Skill added to catalog.");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not add skill.";
      toast.error(msg);
    } finally {
      setBusyLabel("Loading operators…");
    }
  };

  const onEdit = async (row: OperatorRow) => {
    try {
      const editRow = await runWithLoading(async () => {
        await fetchSkillsCatalog();
        return getListById(ENDPOINT.OPERATOR_LIST_BY_ID, row.id);
      });
      const operator = normalizeListPayload(editRow);
      if (!operator) {
        toast.error("Operator not found.");
        return;
      }
      setEditId(operator.id);
      setForm({
        operatorid: operator.operatorid || "",
        fullname: operator.fullname || "",
        experienceYears: String(operator.experienceyears ?? "").trim(),
      });
      setSelectedSkills(parseSkillsCsv(operator.skills));
      setCustomDraft("");
      setAddUpdateModal(true);
    } catch (e) {
      console.log(`Error in edit operator: ${e}`);
      toast.error("Could not load operator.");
    }
  };

  const onDelete = async (row: OperatorRow) => {
    const isConfirmed = await confirm();
    if (!isConfirmed) return;

    try {
      const resp = await updateList(ENDPOINT.OPERATOR_UPDATE, {
        id: row.id,
        active: false,
      } as Record<string, unknown>);
      if (resp === "success") {
        await onLoad(pageIndex, searchText);
      } else {
        toast.error("Could not delete operator.");
      }
    } catch (e) {
      console.log(`Error in delete operator: ${e}`);
      toast.error("Could not delete operator.");
    }
  };

  const onSave = async () => {
    const creating = editId == null;
    setBusyLabel(creating ? "Creating operator…" : "Updating operator…");
    try {
      await runWithLoading(async () => {
        const skillsCsv = serializeSkills(selectedSkills);
        const request: Record<string, unknown> = {
          operatorid: formInputs.operatorid.trim(),
          fullname: formInputs.fullname.trim(),
          skills: skillsCsv,
          experienceyears: Number(formInputs.experienceYears),
          active: true,
        };

        let resp;
        if (creating) {
          resp = await addList(ENDPOINT.OPERATOR_ADD, request);
        } else {
          request.id = editId;
          resp = await updateList(ENDPOINT.OPERATOR_UPDATE, request);
        }

        if (resp === "success") {
          toast.success(
            creating
              ? "Operator created successfully."
              : "Operator updated successfully.",
          );
          handleClose();
          await onLoad(creating ? 0 : pageIndex, searchText);
        } else {
          toast.error(
            creating
              ? "Could not create operator."
              : "Could not update operator.",
          );
        }
      });
    } catch (e) {
      console.log(`Error saving operator: ${e}`);
      toast.error("Could not save operator.");
    } finally {
      setBusyLabel("Loading operators…");
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return;
    }
    void onSave();
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const showPagination = totalPages > 1;
  const hasRecords = operators.length > 0;

  const saveDisabled =
    !formInputs.operatorid.trim() ||
    !formInputs.fullname.trim() ||
    !formInputs.experienceYears.trim();

  return (
    <div className="operators-page">
      {isLoading && <AppLoader text={busyLabel} />}
      <div className="operators-catalog">
          <div className="operators-catalog__shell">
            <div className="operators-catalog__head">
              <button
                type="button"
                className="operators-catalog__add-btn"
                onClick={handleShow}
              >
                <i className="fa fa-plus" aria-hidden />
                Add Operator
              </button>
            </div>

            <div className="operators-catalog__search-wrap">
              <i className="fa fa-search operators-catalog__search-icon" aria-hidden />
              <input
                type="search"
                placeholder="Search Operators"
                className="operators-catalog__search-input"
                value={searchText}
                onChange={(e) => {
                  const v = e.target.value;
                  setSearchText(v);
                  scheduleSearchReload(v);
                }}
                aria-label="Search operators"
              />
            </div>

            {!hasRecords ? (
              !isLoading && (
                <MesEmptyState
                  icon="fa-users"
                  title="No operators found"
                  subtitle="Add operators to capture skills, experience, and shop-floor assignments."
                />
              )
            ) : (
              <>
                <div className="operators-catalog__table-wrap">
                  <table className="operators-catalog__table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Full name</th>
                        <th>Skills</th>
                        <th>Experience</th>
                        <th>Created on</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {operators.map((row) => {
                        const pills = parseSkillsCsv(row.skills);
                        return (
                          <tr key={row.id}>
                            <td className="operators-catalog__cell-id">
                              {row.operatorid ?? "—"}
                            </td>
                            <td className="operators-catalog__cell-name">
                              {row.fullname ?? "—"}
                            </td>
                            <td>
                              {pills.length === 0 ? (
                                <span className="operators-catalog__cell-muted">—</span>
                              ) : (
                                <div className="operators-catalog__skills">
                                  {pills.map((s) => (
                                    <span key={s} className="operators-catalog__skill-pill">
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="operators-catalog__cell-muted">
                              {formatExperience(row.experienceyears)}
                            </td>
                            <td className="operators-catalog__cell-muted">
                              {row.createdate ? (
                                <Moment format="D MMM YYYY">{row.createdate}</Moment>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              <div className="operators-catalog__actions">
                                <button
                                  type="button"
                                  className="operators-catalog__action-btn"
                                  title="Edit"
                                  onClick={() => void onEdit(row)}
                                >
                                  <i className="fa fa-pencil" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  className="operators-catalog__action-btn operators-catalog__action-btn--danger"
                                  title="Delete"
                                  onClick={() => void onDelete(row)}
                                >
                                  <i className="fa fa-trash-o" aria-hidden />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {showPagination && (
                  <div className="operators-catalog__pagination">
                    <nav aria-label="Operators pagination">
                      <ul className="pagination">
                        <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                          <button
                            type="button"
                            className="page-link"
                            disabled={pageIndex === 0}
                            onClick={() => void onLoad(pageIndex - 1)}
                          >
                            &laquo;
                          </button>
                        </li>
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <li
                            key={i}
                            className={`page-item ${i === pageIndex ? "active" : ""}`}
                          >
                            <button
                              type="button"
                              className="page-link"
                              onClick={() => void onLoad(i)}
                            >
                              {i + 1}
                            </button>
                          </li>
                        ))}
                        <li
                          className={`page-item ${
                            pageIndex >= totalPages - 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            disabled={pageIndex >= totalPages - 1}
                            onClick={() => void onLoad(pageIndex + 1)}
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      <Modal
        className="operators-modal"
        size="lg"
        centered
        show={openAddUpdateModal}
        onHide={handleClose}
        backdrop="static"
      >
        <Modal.Header closeButton={false} className="operators-modal__header">
          <Modal.Title as="h2" className="operators-modal__title">
            {editId == null ? "Add Operator" : "Edit Operator"}
          </Modal.Title>
          <button
            type="button"
            className="operators-modal__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        </Modal.Header>
        <Modal.Body className="operators-modal__body">
          <Form
            noValidate
            validated={validated}
            onSubmit={handleSubmit}
            id="operators-form"
          >
            <Row className="g-3 mb-2">
              <Col md={6}>
                <Form.Group controlId="operatorid">
                  <Form.Label>Operator ID</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="operatorid"
                    value={formInputs.operatorid}
                    onChange={handleChange}
                    placeholder="e.g. OP006"
                    disabled={editId != null}
                  />
                  <Form.Control.Feedback type="invalid">
                    Operator ID is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="fullname">
                  <Form.Label>Full name</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="fullname"
                    value={formInputs.fullname}
                    onChange={handleChange}
                    placeholder="Full name"
                  />
                  <Form.Control.Feedback type="invalid">
                    Full name is required.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2" controlId="skills-block">
              <Form.Label>Skills</Form.Label>
              <div className="operators-modal__skills-block">
                {selectedSkills.length > 0 && (
                  <div
                    className="operators-modal__skill-chips"
                    aria-label="Selected skills"
                  >
                    {selectedSkills.map((name) => (
                      <span key={`chip-${name}`} className="operators-modal__skill-chip">
                        <span className="operators-modal__skill-chip__text">{name}</span>
                        <button
                          type="button"
                          className="operators-modal__skill-chip__remove"
                          onClick={() => toggleSkill(name)}
                          aria-label={`Remove ${name}`}
                        >
                          <i className="fa fa-times" aria-hidden />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="operators-modal__skills-scroll">
                  {skillCatalog.length === 0 && (
                    <p className="text-muted small mb-2" style={{ fontStyle: "italic" }}>
                      No catalog skills yet. Add a custom skill below (saved to database),
                      or run the skill catalog migration on the server.
                    </p>
                  )}
                  {skillCatalog.map((row) => {
                    const isSelected = selectedSkills.some(
                      (s) => s.toLowerCase() === row.skillname.toLowerCase(),
                    );
                    return (
                      <div
                        key={row.id}
                        className={`operators-modal__skill-row${
                          isSelected ? " operators-modal__skill-row--selected" : ""
                        }`}
                      >
                        <Form.Check
                          type="checkbox"
                          id={`op-skill-${row.id}`}
                          checked={isSelected}
                          onChange={() => toggleSkill(row.skillname)}
                          label={row.skillname}
                        />
                      </div>
                    );
                  })}
                  {extrasSelected.length > 0 && (
                    <>
                      <div
                        className="text-muted small pt-2 pb-1"
                        style={{ fontSize: "0.7rem", textTransform: "uppercase" }}
                      >
                        Other skills
                      </div>
                      {extrasSelected.map((skill) => (
                        <div
                          key={skill}
                          className="operators-modal__skill-row operators-modal__skill-row--selected"
                        >
                          <Form.Check
                            type="checkbox"
                            id={skillDomId(skill, "op-skill-x")}
                            checked
                            onChange={() => toggleSkill(skill)}
                            label={skill}
                          />
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <div className="operators-modal__custom-row">
                  <Form.Control
                    type="text"
                    placeholder="Add custom skill..."
                    value={customDraft}
                    onChange={(e) => setCustomDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void addCustomSkill();
                      }
                    }}
                    aria-label="Add custom skill"
                  />
                  <button
                    type="button"
                    className="operators-modal__add-skill-btn"
                    onClick={() => void addCustomSkill()}
                    disabled={!customDraft.trim()}
                    aria-label="Add skill"
                  >
                    <i className="fa fa-plus" aria-hidden />
                  </button>
                </div>
                <div className="operators-modal__skill-count">
                  {selectedSkills.length} skill{selectedSkills.length === 1 ? "" : "s"}{" "}
                  selected
                </div>
              </div>
            </Form.Group>

            <Form.Group controlId="experienceYears">
              <Form.Label>Experience (years)</Form.Label>
              <Form.Control
                required
                type="number"
                min={0}
                step={1}
                name="experienceYears"
                value={formInputs.experienceYears}
                onChange={handleChange}
                placeholder="e.g. 12"
              />
              <Form.Control.Feedback type="invalid">
                Enter years of experience.
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="operators-modal__footer">
          <Button
            type="button"
            variant="light"
            className="operators-modal__btn-cancel"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="operators-form"
            className="operators-modal__btn-save"
            disabled={saveDisabled}
          >
            Save Operator
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog />
    </div>
  );
};

export default MesOperators;
